import { IPCManager } from '@acrobits/ipc-sdk';
import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { filter, fromEvent, merge, tap } from 'rxjs';

import { InboxMessagePayload, InboxMessageTypes, InboxPostMessage } from './inbox.models';
import { InboxService } from './inbox.service';

@Injectable({
  providedIn: 'root',
})
export class CSPMiddlewareService {
  private readonly inboxService = inject(InboxService);
  private readonly destroy$ = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly translate = inject(TranslateService);
  private readonly sdkManager = new IPCManager();
  private readonly smartLink = this.document.createElement('a');
  private initialized = false;

  private get connected(): boolean {
    return this.sdkManager?.isConnected ?? false;
  }

  private get visible(): boolean {
    return this.document.visibilityState === 'visible';
  }

  public async init(): Promise<void> {
    if (this.initialized) {
      return;
    }
    const context = await this.sdkManager.determineContext();
    // if the context is 'host' - it means we are in the native app,
    // otherwise we skip setting up the notifications pipeline
    if (context !== 'host') {
      return;
    }
    await this.sdkManager.initiateConnection();
    this.initialized = true;
    this.startNotifications();
  }

  private startNotifications() {
    const notifications$ = this.inboxService.notifications$.pipe(
      filter(() => !this.visible),
      tap((e: InboxPostMessage[]) => {
        if (this.connected) {
          this.sdkManager.badgeUpdate(e.length);
          this.openNotification(e);
        }
      })
    );
    const visibilityReset$ = fromEvent(this.document.defaultView!, 'visibilitychange').pipe(
      tap(() => {
        if (this.connected && this.visible) {
          this.inboxService.clearNotificationCount();
          this.sdkManager.badgeUpdate(0);
        }
      })
    );
    merge(notifications$, visibilityReset$).pipe(takeUntilDestroyed(this.destroy$)).subscribe();
  }

  private openNotification(e: InboxPostMessage[]) {
    if (e.length > 0) {
      this.smartLink.href = this.constructSmartLink(e[e.length - 1]);
      this.smartLink.click();
    }
  }

  private constructSmartLink(message: InboxPostMessage): string {
    let title = '';
    let body = '';

    switch (message.type) {
      case InboxMessageTypes.MESSAGE: {
        title = this.translate.instant('csp.notification.new-message', {
          from: (message.payload as InboxMessagePayload).data.messagedBy,
        });
        body = message.payload.message;
        break;
      }
      case InboxMessageTypes.LIVE_CHAT_REQUEST: {
        title = this.translate.instant('csp.notification.live-chat.request');
        body = this.translate.instant('csp.notification.live-chat.info');
        break;
      }
      case InboxMessageTypes.LEAD: {
        title = this.translate.instant('csp.notification.lead.request');
        body = this.translate.instant('csp.notification.lead.info');
        break;
      }
    }

    return (
      'app:?action=function&id=trigger_notification' +
      `&param%5Btitle%5D=${title}` +
      `&param%5Bmessage%5D=${body}` +
      `&param%5Bfrom%5D=Romulus Chat`
    );
  }

  private async reconnect(): Promise<void> {
    return await this.sdkManager.initiateConnection();
  }
}
